import { Injectable } from '@angular/core';
import {
  COUNTRY_CODE,
  CountryCodesType,
  IShipmentLocationRequest,
  SHIPMENT_LOCATION_TYPE_ID,
} from '@cemex-core/cmx-api';
import { ProductType } from '@cemex-core/cmx-api/models/business-lines/product-type';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { getLegalEntity, State } from '@cemex/cmx-client-layout';
import { Store } from '@ngrx/store';
import ShipmentLocationWorkSheetActions from '@ordersnproducts/modules/multiple-order-online/redux/actions/shipment-location-worksheets.actions';
import {
  BusinessLineLoadAction,
  BusinessLineLoadInitialStateAction,
  BusinessLineSetDirectAction,
} from '@ordersnproducts/modules/order-builder/modules/business-line-selection/actions/business-line.actions';
import {
  ShippingConditionLoadAction,
  ShippingConditionLoadInitialStateAction,
  ShippingConditionSetDirectAction,
} from '@ordersnproducts/modules/order-builder/modules/business-line-selection/actions/shipping-condition.actions';
import { getBusinessLinePage } from '@ordersnproducts/modules/order-builder/reducers';
import { Features } from '@ordersnproducts/features';
import ShipmentLocationActions from 'app/shared/upload-document/redux/actions/shipment-location.actions';
import { selectActiveShipmentLocationId } from 'app/shared/upload-document/redux/selectors/shipment-location.selector';
import { MooResolverService } from 'app/shared/upload-document/services/moo-resolver.service';
import { combineLatest, of, Subject } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { PRODUCT_LINE_ID } from '../../constants/product-lines-ids';
import { CxApiMetadataStrategy } from '../cx-api-metadata/cx-api-metadata.service';
import { CxApiMetadataEntity } from '../cx-api-metadata/store/reducers/cx-api-metadata.reducer';
import { CxProductTypeService } from '../cx-product-types/cx-product-type.service';
import { CxProductType } from '../cx-product-types/models/product-type';
import { CxSalesLocationService } from '../cx-sales-locations/cx-sales-locations.service';
import { CxShipmentLocationService } from '../cx-shipment-locations/cx-shipment-locations.service';
import { CxShipmentLocation } from '../cx-shipment-locations/models/shipment-location';
import { selectCxShipmentLocationsById } from '../cx-shipment-locations/store/selectors/cx-shipment-location.selector';
import { CxShippingConditionService } from '../cx-shipping-conditions/cx-shipping-conditions.service';
import { CxShippingCondition } from '../cx-shipping-conditions/models/shipping-condition';
import { LoadRolesAction } from '../receiving-person/redux/actions/roles.actions';
import { LoadTelephoneCodeAction } from '../receiving-person/redux/actions/telephone.actions';
import {
  JobsiteSetAction,
  LoadJobsiteAddress,
} from '@ordersnproducts/modules/order-builder/modules/location/actions/jobsite-actions/jobsite.actions';
import ProductTypeActions from 'app/shared/upload-document/redux/actions/product-type.actions';
import { selectShippingConditionState } from 'app/shared/upload-document/redux/selectors/shipping-condition.selector';
import { CxCustomerService } from '../cx-customer/cx-customer.service';
import { PRODUCT_LINE_CODE } from '../../constants/product-lines-codes';
import { selectActiveWorksheet } from '@ordersnproducts/modules/multiple-order-online/redux/selectors/shipment-location-worksheets.selector';
import CxCustomerActions from '../cx-customer/store/actions/cx-customer.actions';
import { ORDER_EXPERIENCES } from '../../constants/order-experiences';

@Injectable({
  providedIn: 'root',
})
export class NewOrderFlowService {
  private _newOrderFlowDisabled$ = new Subject<void>();

  constructor(
    private store: Store<State>,
    private featureToggleService: FeatureToggleService,
    private cxCustomerService: CxCustomerService,
    private cxProductTypeService: CxProductTypeService,
    private cxShipmentLocationService: CxShipmentLocationService,
    private cxShippingConditionService: CxShippingConditionService,
    private cxSalesLocationService: CxSalesLocationService,
    private mooResolver: MooResolverService,
  ) {}

  public onNewOrderFlowDisabled(): void {
    this._newOrderFlowDisabled$.next();
  }

  fetchCustomerExperiencesFiltered(productTypeCode) {
    this.selectCustomer()
      .pipe(
        map((legalEntity) =>
          this.store.dispatch(
            CxCustomerActions.fetchCustomerExperiencesFiltered({
              experience: ORDER_EXPERIENCES.CEMENT_UNIVERSAL_LINK,
              customerId: legalEntity.legalEntityId,
              productType: PRODUCT_LINE_CODE.BULK_CEMENT,
            }),
          ),
        ),
      )
      .subscribe();
  }
  public isNewOrderFlowEnabled$() {
    return of(this.featureToggleService.isFeatureEnabled(Features.NEW_ORDER_FLOW)).pipe(
      switchMap((isEnabled) => {
        if (isEnabled) {
          return this.selectCustomer()
            .pipe(
              switchMap((legalEntity) => {
                if (legalEntity?.countryCode !== COUNTRY_CODE.MEXICO) {
                  return combineLatest([
                    of(legalEntity),
                    this.selectCustomerExperiences().pipe(
                      filter(([_, { isLoading, isFetched }]) => !isLoading && isFetched),
                    ),
                  ]);
                } else {
                  // For MX is not necessary to check the experiences
                  // besides the API gives timeout because of the request is for customerId and is affecting the SP performance
                  return combineLatest([of(legalEntity), of([])]);
                }
              }),
              takeUntil(this._newOrderFlowDisabled$),
            )
            .pipe(
              switchMap(([{ legalEntityId, countryCode }, [customerExperiences]]) => {
                const enabledExperiences = customerExperiences?.filter(({ uId, features, isOnline }) => {
                  const productTypeCode = uId.split('_')[1];

                  if (productTypeCode === PRODUCT_LINE_CODE.READY_MIX && isOnline) {
                    const UOFExperience = features.find(({ key }) => key === 'UNIF_ORD');
                    return UOFExperience ? UOFExperience.enabled : false;
                  } else {
                    return false;
                  }
                });

                // For MX always must be enable de new order flow if the rollout is enabled
                // For US and UK only if the customer has the experience RMX_UNIVERSAL_LINK and isOnline and has UNIF_ORD feature
                if (COUNTRY_CODE.MEXICO === countryCode) {
                  return of(true);
                } else if (enabledExperiences.length !== 0) {
                  return this.cxShipmentLocationService
                    .selectShipmentLocations$({
                      legalEntityId: {
                        entityId: legalEntityId,
                        entityTypeId: SHIPMENT_LOCATION_TYPE_ID.CUSTOMER,
                      },
                      shipmentLocationTypeId: SHIPMENT_LOCATION_TYPE_ID.JOBSITE,
                      productLineId: PRODUCT_LINE_ID.READY_MIX,
                      orderBlocked: false,
                      ...this.getShipmentLocationsRequestParams({
                        countryCode: countryCode,
                        productTypeId: PRODUCT_LINE_ID.READY_MIX,
                      })[1],
                    })
                    .pipe(
                      filter(([_, { isLoading, isFetched }]) => !isLoading && isFetched),
                      map(
                        ([shipmentLocations]) =>
                          shipmentLocations.filter(
                            (shipmentLocation) =>
                              shipmentLocation.isMultipleOrder &&
                              enabledExperiences.some(
                                (experience) => shipmentLocation.shipmentLocationCode === experience.uId.split('_')[0],
                              ),
                          ).length !== 0,
                      ),
                    );
                } else {
                  return of(false);
                }
              }),
            );
        } else {
          return of(false);
        }
      }),
      shareReplay(),
    );
  }

  public selectCustomer() {
    return this.store.select(getLegalEntity).pipe(distinctUntilChanged((a, b) => a.legalEntityId === b.legalEntityId));
  }

  public selectCustomerExperiences() {
    return this.selectCustomer().pipe(
      switchMap(({ legalEntityId }) => this.cxCustomerService.selectCustomerExperiences$(legalEntityId)),
    );
  }

  private selectProductTypeEntity(strategy?: CxApiMetadataStrategy) {
    return this.selectCustomer().pipe(
      switchMap((lE) =>
        this.cxProductTypeService.selectProductTypes$(
          {
            customerId: lE.legalEntityId,
          },
          strategy,
        ),
      ),
    );
  }

  private selectSalesRegionEntity(productTypeId: number | string, strategy?: CxApiMetadataStrategy) {
    // unfortunately for mx this patch must be implemented,
    // since although for the user bag cement and multirpdoct are merged,
    // for sales region must be consulted only as bag cement
    productTypeId = productTypeId === '2,3' ? PRODUCT_LINE_ID.BAG_CEMENT : productTypeId;
    return this.cxSalesLocationService.selectSalesLocations$({ productTypeId }, strategy);
  }

  private selectShipmentLocationEntity(productTypeId: number | string, strategy?: CxApiMetadataStrategy) {
    return this.selectCustomer().pipe(
      switchMap(({ legalEntityId, countryCode }) => {
        const requestParams = this.getShipmentLocationsRequestParams({ countryCode, productTypeId });
        if (!requestParams.length) {
          const empty = { entityIds: [], isLoading: false, isFetched: true } as CxApiMetadataEntity;
          return of([[[], empty]] as [CxShipmentLocation[], CxApiMetadataEntity][]);
        }

        /* tslint:disable-next-line */
        return combineLatest(
          requestParams.map((params) => {
            return this.cxShipmentLocationService.selectShipmentLocations$(
              {
                legalEntityId: {
                  entityId: legalEntityId,
                  entityTypeId: SHIPMENT_LOCATION_TYPE_ID.CUSTOMER,
                },
                shipmentLocationTypeId: SHIPMENT_LOCATION_TYPE_ID.JOBSITE,
                productLineId: productTypeId,
                orderBlocked: false,
                ...params,
              },
              strategy,
            );
          }),
        );
      }),
      map((data) => {
        if (data.every(([_, { isFetched }]) => isFetched)) {
          return data.reduce(
            (acc: any, [_, m]) => ({
              ...acc,
              ...m,
              entityIds: Array.from(new Set(acc.entityIds.concat(m.entityIds))),
            }),
            { ...(data[0][1] as CxApiMetadataEntity) },
          );
        }

        return { entityIds: [], isLoading: true, isFetched: false } as CxApiMetadataEntity;
      }),
      switchMap((metadata: CxApiMetadataEntity) => {
        return this.store.select(selectCxShipmentLocationsById(), { ids: metadata.entityIds }).pipe(
          filter((res) => res.length === metadata.entityIds.length),
          map((res) => [res, metadata] as [CxShipmentLocation[], CxApiMetadataEntity]),
        );
      }),
    );
  }

  private selectShippingConditionEntity(productTypeId: number | string, strategy?: CxApiMetadataStrategy) {
    return this.selectCustomer().pipe(
      switchMap((lE) =>
        this.cxShippingConditionService.selectShippingConditions$(
          {
            customerId: lE.legalEntityId,
            productTypeIds: productTypeId,
          },
          strategy,
        ),
      ),
    );
  }

  public selectActiveSalesRegions(productTypeId: number | string) {
    return this.selectSalesRegionEntity(productTypeId).pipe(
      filter(([_, { isFetched }]) => isFetched),
      map(([salesAreas]) => salesAreas),
    );
  }

  public selectActiveShippingConditions(productTypeId: number | string, strategy?: CxApiMetadataStrategy) {
    return this.selectShippingConditionEntity(productTypeId, strategy).pipe(
      filter(([_, { isFetched }]) => isFetched),
      map(([shippingConditions]) => shippingConditions),
    );
  }

  public selectActiveProductTypes() {
    return this.selectProductTypeEntity().pipe(
      filter(([_, { isFetched }]) => isFetched),
      map(([productTypes]) => productTypes),
    );
  }

  public selectActiveShipmentLocations(productTypeId: number | string) {
    return this.selectShipmentLocationEntity(productTypeId).pipe(
      filter(([_, { isFetched }]) => isFetched),
      map(([shipmentLocations]) => shipmentLocations),
    );
  }

  public selectActiveProductTypesLoading() {
    return this.selectProductTypeEntity('cache-only').pipe(map(([_, { isLoading }]) => isLoading));
  }

  public selectActiveShipmentLocationsLoading(productTypeId: number | string) {
    return this.selectShipmentLocationEntity(productTypeId, 'cache-only').pipe(map(([_, { isLoading }]) => isLoading));
  }

  public selectActiveShippingConditionsLoading(productTypeId: number | string) {
    return this.selectShippingConditionEntity(productTypeId, 'cache-only').pipe(map(([_, { isLoading }]) => isLoading));
  }

  public selectActiveSalesAreaLoading(productTypeId: number | string) {
    return this.selectSalesRegionEntity(productTypeId, 'cache-only').pipe(map(([_, { isLoading }]) => isLoading));
  }

  public prepareStateForMOO$({
    shipmentLocation,
    productType,
    shippingCondition,
  }: {
    shipmentLocation: CxShipmentLocation;
    productType: CxProductType;
    shippingCondition: CxShippingCondition;
  }) {
    this.cxProductTypeService.setActive(productType ? productType.productTypeId : null);
    this.cxShipmentLocationService.setActive(shipmentLocation ? shipmentLocation.shipmentLocationId : null);
    this.cxShippingConditionService.setActive(shippingCondition ? shippingCondition.shippingConditionId : null);

    this.mooResolver.resolve();

    return this.store.select(selectShippingConditionState).pipe(
      filter(({ isFetched }) => isFetched),
      take(1),
      tap(() => {
        this.store.dispatch(
          ProductTypeActions.setActive({
            productTypeId: productType.productTypeId as number,
          }),
        );

        this.store.dispatch(
          ShipmentLocationActions.setActive({
            shipmentLocationId: shipmentLocation.shipmentLocationId,
          }),
        );
        this.store.dispatch(
          ShipmentLocationWorkSheetActions.setActive({ shipmentLocationId: shipmentLocation.shipmentLocationId }),
        );

        this.store.dispatch(new LoadJobsiteAddress(shipmentLocation.address.addressId));
        this.store.dispatch(new JobsiteSetAction(shipmentLocation));
      }),
      switchMap(() =>
        combineLatest([this.store.select(selectActiveShipmentLocationId), this.store.select(selectActiveWorksheet)]),
      ),
      filter(([a1, a2]) => !a1 && !a2),
      delay(300),
    );
  }

  public prepareStateForSO$({
    shipmentLocation,
    productType,
    shippingCondition,
  }: {
    shipmentLocation: CxShipmentLocation;
    productType: CxProductType;
    shippingCondition: CxShippingCondition;
  }) {
    this.cxProductTypeService.setActive(productType ? productType.productTypeId : null);
    this.cxShipmentLocationService.setActive(shipmentLocation ? shipmentLocation.shipmentLocationId : null);
    this.cxShippingConditionService.setActive(shippingCondition ? shippingCondition.shippingConditionId : null);

    return this.selectCustomer().pipe(
      take(1),
      tap((lE) => {
        // Predefine Business Line Step
        this.store.dispatch(new LoadRolesAction());
        this.store.dispatch(new LoadTelephoneCodeAction());

        this.store.dispatch(new BusinessLineLoadInitialStateAction());
        this.store.dispatch(new BusinessLineLoadAction(lE));

        this.store.dispatch(new ShippingConditionLoadInitialStateAction());
        this.store.dispatch(
          new ShippingConditionLoadAction({
            customerId: lE.legalEntityId,
            productTypeId: productType.productTypeId as any,
          }),
        );
      }),
      switchMap(() =>
        this.store.select(getBusinessLinePage).pipe(
          filter(
            ({ businessLine: _businessLine, shippingCondition: _shippingCondition }) =>
              _businessLine.loaded && _shippingCondition.loaded,
          ),
          tap(() => {
            this.store.dispatch(new BusinessLineSetDirectAction(productType as ProductType));
            this.store.dispatch(new ShippingConditionSetDirectAction(shippingCondition));
          }),
          filter(
            ({ businessLine: _businessLine, shippingCondition: _shippingCondition }) =>
              !!(_businessLine.selected && _shippingCondition.selected),
          ),
        ),
      ),
    );
  }

  public prepareStateForBulkCementOnline({ productType, shippingCondition }) {
    this.cxProductTypeService.setActive(productType ? productType.productTypeId : null);
    this.cxShippingConditionService.setActive(shippingCondition ? shippingCondition.shippingConditionId : null);
  }

  private getShipmentLocationsRequestParams({
    countryCode,
    productTypeId,
  }: {
    countryCode: CountryCodesType;
    productTypeId: string | number;
  }): IShipmentLocationRequest[] {
    return (
      {
        // UK
        [`${COUNTRY_CODE.UNITED_KINGDOM}_${PRODUCT_LINE_ID.READY_MIX}`]: [
          { hasDocuments: true },
          { isOnline: true, hasDocuments: true },
        ],
        [`${COUNTRY_CODE.UNITED_KINGDOM}_${PRODUCT_LINE_ID.BULK_CEMENT}`]: [
          { hasDocuments: false },
          { isOnline: true, hasDocuments: true },
        ],
        [`${COUNTRY_CODE.UNITED_KINGDOM}_${PRODUCT_LINE_ID.BAG_CEMENT}`]: [
          { hasDocuments: false },
          { isOnline: true, hasDocuments: true },
        ],
        // US
        [`${COUNTRY_CODE.USA}_${PRODUCT_LINE_ID.READY_MIX}`]: [
          { hasDocuments: true },
          { isOnline: true, hasDocuments: true },
        ],
        [`${COUNTRY_CODE.USA}_${PRODUCT_LINE_ID.BULK_CEMENT}`]: [
          { hasDocuments: false },
          { isOnline: true, hasDocuments: true },
        ],
        [`${COUNTRY_CODE.USA}_${PRODUCT_LINE_ID.BAG_CEMENT}`]: [
          { hasDocuments: false },
          { isOnline: true, hasDocuments: true },
        ],
        // MX
        [`${COUNTRY_CODE.MEXICO}_${PRODUCT_LINE_ID.READY_MIX}`]: [
          { hasDocuments: true },
          { isOnline: true, hasDocuments: true },
        ],
      }[`${countryCode}_${productTypeId}`] || []
    );
  }
}
